import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex w-full h-full" }
const _hoisted_2 = { class: "flex flex-col w-full h-full justify-between" }
const _hoisted_3 = { class: "flex flex-col items-center justify-center" }
const _hoisted_4 = { class: "font-sourcesanspro-semibold text-[40px] mb-4" }
const _hoisted_5 = { class: "font-sourcesanspro-regular text-m2" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "flex flex-col mx-[34px] p-4 mb-2 bg-white/10 rounded-lg" }
const _hoisted_8 = {
  key: 0,
  class: "font-sourcesanspro-semibold text-m2"
}
const _hoisted_9 = { class: "flex flex-col w-full px-[34px] align-center mb-[34px]" }
const _hoisted_10 = { class: "flex w-full items-center justify-between" }

import { onMounted, ref } from 'vue';
import { IonButton, IonContent, IonIcon } from '@ionic/vue';
import { arrowForwardOutline, checkmarkCircle } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import router from '@/router';

import ItemsDetails from '@/shared/components/ui/ItemsDetails.vue';
import TopAlert from '@/shared/components/ui/TopAlert.vue';
import useTenant from '@/shared/composables/useTenant';
import useWallet from '@/modules/wallet/composables/useWallet';


export default /*@__PURE__*/_defineComponent({
  __name: 'TransactionPageConfirmation',
  setup(__props) {

const { t } = useI18n();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { getPublicOrder } = useWallet();
const orderId = router.currentRoute.value.params.orderId?.toString() || '';
const orderMapped = ref<any>(null);

onMounted(async () => {
	const order = await getPublicOrder(orderId);
	orderMapped.value = mapPublicOrderToTransaction(order, t);
});

const numberValue = (value: any): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const orderCurrency = (data: any) => {
	return data?.overview?.currency?.[0] || {
		name: 'EUR',
		prefix: '€',
		exchange_rate: 1,
	};
};

const mapCompletedPurchaseOrderToTransaction = (data: any, t: any) => {
	let totalSum = 0;
	let adminFeeAmount = 0;
	const items = data.overview.items.map((item: any) => {
		const currency = item.currency || orderCurrency(data);
		const exchangeRate = numberValue(currency.exchange_rate) || 1;
		const quantity = numberValue(item.quantity) || 1;
		const unitPrice = numberValue(item.price);
		const itemPrice = unitPrice * quantity;

		totalSum += itemPrice * exchangeRate;

		return {
			description: item.label || t('checkout.item'),
			itemPrice,
			unitPrice,
			currency: currency.prefix || '€',
			quantity,
		};
	});

	adminFeeAmount = Math.max(numberValue(data.app_service_cost), numberValue(data.funding_app_service_cost));
	if (adminFeeAmount > 0) {
		totalSum += adminFeeAmount;
		items.push({
			description: t('transaction.administrative_fee'),
			itemPrice: adminFeeAmount,
			currency: '€',
			quantity: 1,
		});
	}

	return {
		items,
		showTotalPriceString: true,
		totalPriceNumber: totalSum,
		totalDescription: t('transaction.total'),
		totalPriceWithoutFee: totalSum - adminFeeAmount,
		subtitle: t('checkout.transaction.generic.subtitle'),
		title: `€ ${totalSum.toFixed(2)}`,
		totalPriceString: `€${totalSum.toFixed(2)}`,
	};
};

const mapPublicOrderToTransaction = (data: any, t: any) => {
	if (data.completed_purchase_order?.overview?.items?.length) {
		return mapCompletedPurchaseOrderToTransaction(data.completed_purchase_order, t);
	}

	let totalSum = 0;
	let adminFeeAmount = 0;
	const currency = orderCurrency(data);
	const exchangeRate = numberValue(currency.exchange_rate) || 1;
	const transactions = data.type === 'topup'
		? data.overview.transactions.filter((item: any) => ['top_up', 'top_up_cost'].includes(item.type))
		: data.overview.transactions;

	const items = transactions.map((item: any) => {
		const description = (() => {
			switch (item.type) {
				case 'top_up':
					return `${currency.prefix} ${currency.name === 'EUR' ? numberValue(item.amount).toFixed(2) : numberValue(item.amount).toFixed(0)} ${currency.name}`;
				case 'top_up_cost':
					adminFeeAmount = numberValue(item.amount);
					return t('transaction.administrative_fee');
				default:
					return t('checkout.item');
			}
		})();

		let itemPrice = 0;
		if (item.type !== 'top_up_cost') {
			totalSum += numberValue(item.amount) * exchangeRate;
			itemPrice = numberValue(item.amount) * exchangeRate;
		} else {
			totalSum += numberValue(item.amount);
			itemPrice = numberValue(item.amount);
		}

		return {
			description: description,
			itemPrice: itemPrice,
			currency: '€',
			quantity: 1,
		};
	});

	return {
		items,
		showTotalPriceString: true,
		totalPriceNumber: totalSum,
		totalDescription: t('transaction.total'),
		totalPriceWithoutFee: totalSum - adminFeeAmount,
		subtitle: t('top_up.success.subtitle'),
		title: `€ ${totalSum}`,
		totalPriceString: `€${totalSum.toFixed(2)}`,
	};
};

const goTo = () => {
	if (tenantConfig?.value?.main_domain) {
		location.href = `https://${tenantConfig?.value?.main_domain}/#/dashboard?clearCart=true`;
	}
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(TopAlert, { class: "mb-2 pt-[75px] pb-12 bg-anykrowd-green" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_unref(IonIcon), {
                  icon: _unref(checkmarkCircle),
                  class: "text-white text-[78px] mb-2"
                }, null, 8, ["icon"]),
                _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(t)('checkout.transaction.generic.title')), 1),
                _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('checkout.transaction.generic.subtitle')), 1)
              ])
            ]),
            _: 1
          }),
          (_unref(tenantConfig)?.top_up_confirmation_message_title)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  (_unref(tenantConfig)?.top_up_confirmation_message_title)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(t)(_unref(tenantConfig).top_up_confirmation_message_title)), 1))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            (orderMapped.value)
              ? (_openBlock(), _createBlock(ItemsDetails, {
                  key: 0,
                  "border-class": "border-y border-y-slate-200",
                  class: "mb-4 bg-white rounded-xl",
                  "text-class": "text-dark-blue",
                  "text-class-two": "text-dark-blue/70",
                  title: _unref(t)('transaction.order_details'),
                  items: 
							orderMapped.value.items.map((item) => {
								return {
									itemId: item.itemId,
									title: item.description,
									price: item.unitPrice,
									quantity: item.quantity,
									hideQuantity: item.hideQuantity,
									hidePrice: item.hidePrice,
									totalPrice: item.itemPrice,
									discount: item.discount,
									currency: item.currency,
								};
							})
						,
                  "total-title": orderMapped.value.totalDescription,
                  "total-price": orderMapped.value.totalPriceNumber,
                  "total-price-string": orderMapped.value.totalPriceString || '0',
                  "show-total-price-string": orderMapped.value.showTotalPriceString
                }, null, 8, ["title", "items", "total-title", "total-price", "total-price-string", "show-total-price-string"]))
              : _createCommentVNode("", true),
            _createVNode(_unref(IonButton), {
              class: "w-full hover:bg-white hover:rounded-full",
              expand: "full",
              size: "large",
              shape: "round",
              onClick: goTo
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_10, [
                  _createTextVNode(_toDisplayString(_unref(t)('transaction.continue')) + " ", 1),
                  _createVNode(_unref(IonIcon), {
                    icon: _unref(arrowForwardOutline),
                    class: "text-dark-blue mr-2"
                  }, null, 8, ["icon"])
                ])
              ]),
              _: 1
            })
          ])
        ])
      ])
    ]),
    _: 1
  }))
}
}

})